import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserMinus } from "@fortawesome/free-solid-svg-icons";
import IconButton from "../../../components/LogoButton/IconButton";
import { ConfirmationGuard } from "../../../components/ConfirmationGuard";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { enviroment } from "../../../../enviroment";
import {
  unassignConfirmText,
  unassignCustomerState,
} from "../models/logoUnassign";

// "Unassign customer" (flag REACT_APP_LOGO_UNASSIGN_ENABLED): the
// counterpart of AssignCustomerButton - shown when the logo HAS a customer
// org (the other button shows when it has none), with Assign's exclusions
// and two more (unassignCustomerState: an explicit Admin/Vendor role, no
// global logos). NAV's main customer cannot be unlinked in KitMaker (the API
// answers 409), so that case gets an explanatory line instead of a button,
// like the global-logo line under Delete. The click opens the same confirm
// dialog Delete uses; its text carries the NAV-filter warning because the
// popup cannot tell the link's source without an extra GET. Flag off (or no
// handler) = nothing rendered.
export function UnassignCustomerButton({
  logo,
  onUnassign,
}: {
  logo: BaseLogo;
  onUnassign?: () => Promise<void>;
}) {
  const { role } = useProfileContext();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  if (!enviroment.logoUnassignEnabled || !onUnassign) return null;
  const unassign = onUnassign;

  const state = unassignCustomerState(logo, role);
  if (state === "hidden") return null;
  if (state === "nav") {
    return (
      <p className="text-xs text-gray-600 italic text-center">
        {t("logoList.unassignCustomerNav")}
      </p>
    );
  }

  async function handleAccept() {
    setBusy(true);
    try {
      await unassign();
    } finally {
      setBusy(false);
    }
  }

  return (
    <ConfirmationGuard
      anchor={
        <IconButton
          key={"unassign-customer"}
          icon={<FontAwesomeIcon icon={faUserMinus} />}
          width={"full"}
          disabled={busy}
        >
          {t("logoList.unassignCustomer")}
        </IconButton>
      }
      text={unassignConfirmText(logo, t)}
      onAccept={handleAccept}
    />
  );
}
